import type { ManualDashboardPayload, MetricDef, MetricFormat, SeriesPoint } from '../../types/manualDashboard';
import type { SelectOption, SparklinePoint } from '../../ui-kit';

/** The cohort groupings a section can switch to, as select options. */
export function cohortOptions(payload: ManualDashboardPayload): SelectOption[] {
  return (payload.available_grains ?? []).map((g) => ({
    value: g.key,
    label: g.label,
    description: `${g.periods} cohorts${g.key === payload.grain ? ' · the default for this range' : ''}`,
  }));
}

/** Footer text for a trend, or null when every cohort has settled. */
export function maturityNote(points: SeriesPoint[], metric: MetricDef): string | null {
  const n = points.filter((p) => p.provisional).length;
  if (!n) return null;
  // With no waiting period, only a cohort still under way is dashed.
  if (!metric.maturity_days) {
    return `Dashed: ${n > 1 ? `${n} cohorts are` : 'the newest cohort is'} still under way, so ${n > 1 ? 'they hold' : 'it holds'} only some of ${n > 1 ? 'their' : 'its'} days.`;
  }
  return `Dashed: ${n} cohort${n > 1 ? 's' : ''} younger than ${metric.maturity_days} days, `
    + `so ${metric.label.toLowerCase()} has not settled.`;
}

/** A cohort series in the shape the kit's Sparkline takes. */
export function toSparklinePoints(points: SeriesPoint[]): SparklinePoint[] {
  return points.map((p) => ({ key: p.period, label: p.label, value: p.value, provisional: p.provisional }));
}

/** Format one value for its metric's declared format.
 *
 *  Compact money ($1.2M / $340K) everywhere except `money0`, which is a deal
 *  size — those are read against each other and want the exact figure. */
export function formatValue(value: number | null | undefined, format: MetricFormat = 'int'): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  switch (format) {
    case 'money':
      if (Math.abs(value) >= 1_000_000) return `$${(value / 1_000_000).toFixed(2)}M`;
      if (Math.abs(value) >= 1_000) return `$${Math.round(value / 1_000)}K`;
      return `$${Math.round(value).toLocaleString('en-US')}`;
    case 'money0':
      return `$${Math.round(value).toLocaleString('en-US')}`;
    case 'pct':
      return `${value.toFixed(1)}%`;
    case 'days':
      return value.toFixed(1);
    case 'ratio':
      return value.toFixed(2);
    case 'int':
    default:
      return Math.round(value).toLocaleString('en-US');
  }
}

export interface Delta {
  pct: number;
  from: SeriesPoint;
  to: SeriesPoint;
}

/** Change between the last two settled points.
 *
 *  Provisional points are skipped: comparing a half-finished cohort against a
 *  complete one always reads as a collapse, which is the single easiest way
 *  for a cohort dashboard to mislead. Both endpoints are returned so the UI
 *  can name which periods it actually compared - "22.1%" on its own leaves the
 *  reader guessing at the baseline. */
export function trailingDeltaDetail(points: SeriesPoint[] | null | undefined): Delta | null {
  if (!points) return null;
  const settled = points.filter((p) => !p.provisional && p.value !== null);
  if (settled.length < 2) return null;
  const from = settled[settled.length - 2];
  const to = settled[settled.length - 1];
  if (!from.value) return null;
  return { pct: (((to.value as number) - from.value) / Math.abs(from.value)) * 100, from, to };
}

export function trailingDelta(points: SeriesPoint[] | null | undefined): number | null {
  return trailingDeltaDetail(points)?.pct ?? null;
}

/** For most metrics up is good; for these, down is. */
const LOWER_IS_BETTER = new Set([
  'days_to_decision',
  'days_to_policy_eff',
  'decline_rate',
  'decline_count',
  'decline_premium',
  'decline_premium_rate',
  'pending_rate',
  'pending_premium_rate',
  'and_rate',
  'and_premium_rate',
  // Scorecard
  'not_interested_pct',
  'bad_lead_pct',
  'average_lead_age',
]);

export function isImprovement(metricKey: string, delta: number): boolean {
  return LOWER_IS_BETTER.has(metricKey) ? delta < 0 : delta > 0;
}

export function countProvisional(points: SeriesPoint[] | null | undefined): number {
  return (points ?? []).filter((p) => p.provisional).length;
}
